import algoliasearch from 'algoliasearch/lite';
import classNames from 'classnames';
import { StoryblokComponent, storyblokEditable } from 'gatsby-source-storyblok';
import React, {
  Suspense,
  memo,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Headroom from 'react-headroom';
import { useMediaQuery } from 'react-responsive';
import HeaderContext from '../../contexts/Header.context';
import { PageContext } from '../../contexts/PageContext';
import { useScrollLock } from '../../hooks/useScrollLock.hook';
import { Blok } from '../../interfaces/Blok.interface';
import { CTA } from '../../interfaces/CTA.interface';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import { Theme } from '../../interfaces/enums/Theme.enum';
import '../../styles/headroom.css';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import { ButtonLinkProps } from '../ButtonLink/ButtonLink';
import HeaderSearch from './HeaderSearch/HeaderSearch';
import MainNav from './MainNav/MainNav';
import { MainNavLink } from './MainNav/MainNav.interface';
import PreNav from './PreNav/PreNav';
import { PreNavItem } from './PreNav/PreNav.interface';
import SideNav from './SideNav/SideNav';

export enum VisibleSideNavType {
  None,
  Menu,
  Search,
}

export interface SideNavStatus {
  isVisible: boolean;
  sideNavType: VisibleSideNavType;
}

export interface HeaderBlokProps {
  company_logo: StoryblokAsset;
  downtime_banner: Blok[];
  pre_navigation_items: PreNavItem[];
  main_navigation_items: MainNavLink[];
  primary_cta: Blok[];
  mobile_primary_cta: Blok[];
  mobile_secondary_cta: Blok[];
  secondary_cta: ButtonLinkProps[];
  enable_search: boolean;
  search_heading: string;
  search_bar_placeholder: string;
  permitted_content_types: string[];
  search_view_more_cta: CTA[];
  search_no_results_found_title: string;
  search_no_results_found_body?: string;
  structured_data: Blok[];
  isFixed?: boolean;
}

interface HeaderProps {
  blok: HeaderBlokProps;
}

const getSearch = (
  isHeaderSearchFunctionalityRequired: boolean,
  heading: string | undefined,
  placeholder: string,
  permittedContentTypes: string[],
  cta: Blok[],
  noResultsTitle: string,
  noResultsBody: string | undefined
): JSX.Element => {
  return isHeaderSearchFunctionalityRequired ? (
    <Suspense>
      <HeaderSearch
        searchHeading={heading}
        searchBarPlaceholder={placeholder}
        permittedContentTypes={permittedContentTypes}
        searchViewMoreCTA={cta}
        searchNoResultsFoundTitle={noResultsTitle}
        searchNoResultsFoundBody={noResultsBody}
      />
    </Suspense>
  ) : (
    <></>
  );
};

const getPreviouslyDismissedDowntimeBannerIdsFromLocalStorage =
  (): string[] => {
    const previouslyDismissedDowntimeBannerIdsAsString: string | null =
      localStorage.getItem('previouslyDismissedDowntimeBannerIds');
    if (!previouslyDismissedDowntimeBannerIdsAsString) return [];
    return previouslyDismissedDowntimeBannerIdsAsString.split(',');
  };

const Header: React.FC<HeaderProps> = ({ blok }) => {
  const {
    company_logo,
    downtime_banner,
    pre_navigation_items,
    main_navigation_items,
    mobile_primary_cta,
    mobile_secondary_cta,
    primary_cta,
    secondary_cta,
    enable_search,
    search_heading = undefined,
    search_bar_placeholder,
    permitted_content_types,
    search_view_more_cta,
    search_no_results_found_title,
    search_no_results_found_body = undefined,
    structured_data = [],
    isFixed = true,
  } = blok;

  const [isQuoteEmbed, setIsQuoteEmbed] = useState(false);
  useEffect(() => {
    if (sessionStorage.getItem('quote-embed') === 'true') {
      setIsQuoteEmbed(true);
    }
  }, []);

  const { headerIsTransparent, isHeaderSearchFunctionalityRequired } =
    useContext(PageContext);
  const [navIsFixed, setNavIsFixed] = useState<boolean>(false);
  const [navIsUnpinned, setNavIsUnpinned] = useState<boolean>(false);
  const [sideNavStatus, setSideNavStatus] = useState<SideNavStatus>({
    isVisible: false,
    sideNavType: VisibleSideNavType.None,
  });
  const [isDesktopSearchDropdownVisible, setIsDesktopSearchDropdownVisible] =
    useState<boolean>(false);

  const [
    previouslyDismissedDowntimeBannerIds,
    setPreviouslyDismissedDowntimeBannerIds,
  ] = useState<string[]>([]);

  const toggleSideNav = (
    isVisible: boolean,
    sideNavType: VisibleSideNavType = VisibleSideNavType.None
  ): void => setSideNavStatus({ isVisible, sideNavType });

  const toggleIsDesktopSearchDropdownVisible = (): void =>
    setIsDesktopSearchDropdownVisible(!isDesktopSearchDropdownVisible);

  const { lockScroll, unlockScroll } = useScrollLock();

  const indexName: string = useMemo(() => {
    return `${process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME}`;
  }, [process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME]);

  const algoliaSearchClient = useMemo(() => {
    return algoliasearch(
      `${process.env.GATSBY_ALGOLIA_APP_ID}`,
      `${process.env.GATSBY_ALGOLIA_SEARCH_API_KEY}`
    );
  }, [
    process.env.GATSBY_ALGOLIA_APP_ID,
    process.env.GATSBY_ALGOLIA_SEARCH_API_KEY,
  ]);

  const overriddenSearchClient: any = useMemo(() => {
    return {
      ...algoliaSearchClient,
      search(requests: any[]) {
        if (requests.every(({ params }) => !params.query)) {
          return Promise.resolve({
            results: requests.map(() => ({
              hits: [],
              nbHits: 0,
              nbPages: 0,
              page: 0,
              processingTimeMS: 0,
              hitsPerPage: 0,
              exhaustiveNbHits: false,
              query: '',
              params: '',
            })),
          });
        }
        return algoliaSearchClient.search(requests);
      },
    };
  }, [algoliaSearchClient]);

  useEffect(() => {
    const previouslyDismissedDowntimeBannerIdsFromLocalStorage: string[] =
      getPreviouslyDismissedDowntimeBannerIdsFromLocalStorage();
    if (previouslyDismissedDowntimeBannerIdsFromLocalStorage)
      setPreviouslyDismissedDowntimeBannerIds(
        previouslyDismissedDowntimeBannerIdsFromLocalStorage
      );
  }, []);

  useEffect(() => {
    const previouslyDismissedDowntimeBannerIdsFromLocalStorage: string[] =
      getPreviouslyDismissedDowntimeBannerIdsFromLocalStorage();
    if (
      previouslyDismissedDowntimeBannerIds !==
      previouslyDismissedDowntimeBannerIdsFromLocalStorage
    ) {
      const previouslyDismissedDowntimeBannerIdsAsString: string =
        previouslyDismissedDowntimeBannerIds.join(',');
      localStorage.setItem(
        'previouslyDismissedDowntimeBannerIds',
        previouslyDismissedDowntimeBannerIdsAsString
      );
    }
  }, [previouslyDismissedDowntimeBannerIds]);

  useEffect(() => {
    sideNavStatus.isVisible ? lockScroll() : unlockScroll();
  }, [sideNavStatus.isVisible]);

  const [scrollPosition, setScrollPosition] = useState(0);
  const handleScroll = () => {
    const position = window.scrollY;
    setScrollPosition(position);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const isheaderFixed = scrollPosition > 64;
  const search = useMemo(
    () =>
      getSearch(
        isHeaderSearchFunctionalityRequired,
        search_heading,
        search_bar_placeholder,
        permitted_content_types,
        search_view_more_cta,
        search_no_results_found_title,
        search_no_results_found_body
      ),
    [
      isHeaderSearchFunctionalityRequired,
      search_heading,
      search_bar_placeholder,
      permitted_content_types,
      search_view_more_cta,
      search_no_results_found_title,
      search_no_results_found_body,
    ]
  );

  const sm = useMediaQuery({
    query: '(max-width: 639px)',
  });
  const desktop = useMediaQuery({
    query: '(min-width: 640px)',
  });

  return (
    <header
      {...storyblokEditable(blok)}
      className={isQuoteEmbed ? 'hidden' : ''}
    >
      {/*-- structured data schema markup --*/}
      {structured_data?.map((structuredData: Blok) => (
        <StoryblokComponent
          blok={{ ...structuredData, logo: company_logo }}
          key={structuredData._uid}
        />
      ))}
      <HeaderContext.Provider
        value={{
          userHasScrolled: navIsFixed,
          navIsUnpinned: navIsUnpinned,
        }}
      >
        {downtime_banner && (
          <>
            {downtime_banner.map((banner: Blok) => (
              <>
                {!previouslyDismissedDowntimeBannerIds.includes(
                  banner._uid
                ) && (
                  <StoryblokComponent
                    blok={{
                      ...banner,
                      dismissButtonWasClicked: (bannerId: string) =>
                        setPreviouslyDismissedDowntimeBannerIds(
                          (previouslyDismissedDowntimeBannerIds) => [
                            ...previouslyDismissedDowntimeBannerIds,
                            bannerId,
                          ]
                        ),
                    }}
                    key={banner._uid}
                  />
                )}
              </>
            ))}
          </>
        )}
        <Headroom
          onPin={() => {
            setNavIsFixed(true);
            setNavIsUnpinned(false);
          }}
          onUnpin={() => {
            setNavIsFixed(true);
            setNavIsUnpinned(true);
          }}
          onUnfix={() => {
            setNavIsFixed(false);
          }}
          style={{ zIndex: '40' }}
          className={classNames({ 'headroom-static': desktop })}
          disable={!isFixed ? true : desktop}
        >
          {pre_navigation_items && (
            <PreNav
              pre_navigation_items={pre_navigation_items}
              secondary_cta={secondary_cta}
            ></PreNav>
          )}
          <div
            className={classNames('h-[153px] sm:h-[119px]', {
              'xl:h-auto': !isheaderFixed,
            })}
          />
          <div
            className={classNames(
              `sm:h-[119px] fixed xl:relative top-0 left-0 w-full z-40 prose-p:transition-all prose-a:transition-all`,
              { 'xl:!fixed': isheaderFixed }
            )}
          >
            {main_navigation_items && (
              <MainNav
                company_logo={company_logo}
                main_navigation_items={main_navigation_items}
                primary_cta={primary_cta}
                enable_search={enable_search}
                search={search}
                needsTransparentHeader={headerIsTransparent}
                toggleSideNav={toggleSideNav}
                toggleIsDesktopSearchDropdownVisible={
                  toggleIsDesktopSearchDropdownVisible
                }
                searchIndexName={indexName}
                overriddenSearchClient={overriddenSearchClient}
                searchPermittedContentTypes={permitted_content_types}
              />
            )}
          </div>
        </Headroom>
      </HeaderContext.Provider>

      {main_navigation_items && pre_navigation_items && (
        <SideNav
          sideNavStatus={sideNavStatus}
          pre_navigation_items={pre_navigation_items}
          main_navigation_items={main_navigation_items}
          mobile_primary_cta={mobile_primary_cta}
          mobile_secondary_cta={mobile_secondary_cta}
          secondary_cta={secondary_cta}
          search_heading={search_heading}
          search={search}
          closeSideNav={() => toggleSideNav(false)}
          searchIndexName={indexName}
          overriddenSearchClient={overriddenSearchClient}
          searchPermittedContentTypes={permitted_content_types}
        ></SideNav>
      )}
    </header>
  );
};

export default memo(withBlokWrapper(Header, Theme.DARK));
